import { useState, useRef } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Upload, Download, FileSpreadsheet, X, CheckCircle2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";


/**
 * "Import inventory from CSV" reusable dialog.
 *
 * Workflow:
 *  1. User downloads the CSV template (link below).
 *  2. User picks a CSV file.
 *  3. We POST multipart/form-data to /api/items/import.
 *  4. We render a result summary with created / updated / skipped counts
 *     and any row-level errors.
 */
export function ImportItemsDialog({ open, onOpenChange }) {
  const qc = useQueryClient();
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const importMut = useMutation({
    mutationFn: (f) => api.importItemsCsv(f),
    onSuccess: (data) => {
      setResult(data);
      qc.invalidateQueries({ queryKey: ["items"] });
      qc.invalidateQueries({ queryKey: ["dashboard"] });
      const total = (data.created || 0) + (data.updated || 0);
      if (total > 0) {
        toast.success(`Imported ${data.created} new · updated ${data.updated}` + (data.skipped ? ` · skipped ${data.skipped}` : ""));
      } else {
        toast.warning("No rows were imported. Check the error list below.");
      }
    },
    onError: (e) => {
      toast.error(e?.response?.data?.detail || "Import failed");
    },
  });

  function reset() {
    setFile(null);
    setResult(null);
    if (inputRef.current) inputRef.current.value = "";
  }

  function handleClose(o) {
    if (!o) reset();
    onOpenChange(o);
  }

  function submit() {
    if (!file) {
      toast.error("Choose a CSV file first");
      return;
    }
    importMut.mutate(file);
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent data-testid="import-items-dialog" className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Upload className="size-4 text-primary" /> Import inventory from CSV
          </DialogTitle>
          <DialogDescription className="text-xs">
            Upload a CSV of items to bulk-create or update your catalog. Rows
            matched by <span className="mono">sku</span> are updated; others
            are created. Empty cells on existing items are preserved.
          </DialogDescription>
        </DialogHeader>

        {!result && (
          <div className="space-y-4 pt-1">
            <div className="rounded-md bg-slate-50 border border-border p-3 text-xs space-y-1">
              <p className="font-semibold mb-1">Expected columns (header row required):</p>
              <p className="mono leading-relaxed">name (required), specification, sku, unit, stock_qty, reorder_level, price, category, type, brand, model, warranty_months, support_months, notes</p>
              <a
                data-testid="import-items-template-link"
                href={`${API}/items/import/template`}
                className="inline-flex items-center gap-1 text-primary underline mt-2"
                target="_blank" rel="noreferrer"
              >
                <Download className="size-3" /> Download template
              </a>
            </div>

            <div
              data-testid="import-items-dropzone"
              className="border-2 border-dashed border-border rounded-md p-6 text-center cursor-pointer hover:border-primary transition-colors"
              onClick={() => inputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                const f = e.dataTransfer?.files?.[0];
                if (f) setFile(f);
              }}
            >
              <FileSpreadsheet className="size-8 text-muted-foreground mx-auto mb-2" />
              {file ? (
                <div className="space-y-1">
                  <p className="text-sm font-medium" data-testid="import-items-file-name">{file.name}</p>
                  <p className="text-[11px] text-muted-foreground">{(file.size / 1024).toFixed(1)} KB</p>
                  <button
                    type="button"
                    data-testid="import-items-clear-file"
                    className="text-[11px] underline text-rose-600 mt-1"
                    onClick={(e) => { e.stopPropagation(); setFile(null); if (inputRef.current) inputRef.current.value = ""; }}
                  >Remove file</button>
                </div>
              ) : (
                <>
                  <p className="text-sm">Click to choose, or drop a CSV here</p>
                  <p className="text-[10px] text-muted-foreground mt-1">UTF-8 encoded, max ~10MB</p>
                </>
              )}
              <input
                ref={inputRef}
                data-testid="import-items-file-input"
                type="file"
                accept=".csv,text/csv"
                className="hidden"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
            </div>
          </div>
        )}

        {result && (
          <ImportResultView result={result} />
        )}

        <DialogFooter>
          {result ? (
            <>
              <Button variant="ghost" onClick={reset} data-testid="import-items-reset">
                Import another file
              </Button>
              <Button onClick={() => handleClose(false)} data-testid="import-items-done">
                Done
              </Button>
            </>
          ) : (
            <>
              <Button variant="ghost" onClick={() => handleClose(false)} data-testid="import-items-cancel" disabled={importMut.isPending}>
                <X className="size-4 mr-1.5" /> Cancel
              </Button>
              <Button
                data-testid="import-items-submit"
                onClick={submit}
                disabled={!file || importMut.isPending}
                className="bg-primary hover:bg-primary/90"
              >
                <Upload className="size-4 mr-1.5" />
                {importMut.isPending ? "Importing…" : "Import"}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ImportResultView({ result }) {
  const errs = result.errors || [];
  const hasSuccess = (result.created || 0) + (result.updated || 0) > 0;
  return (
    <div data-testid="import-items-result" className="space-y-3 pt-1">
      <div className="grid grid-cols-3 gap-2">
        <StatCard label="Created" value={result.created || 0} color="text-emerald-700" bg="bg-emerald-50" />
        <StatCard label="Updated" value={result.updated || 0} color="text-primary" bg="bg-amber-50" />
        <StatCard label="Skipped" value={result.skipped || 0} color="text-amber-700" bg="bg-amber-50" />
      </div>

      {hasSuccess && (
        <div className="rounded-md bg-emerald-50 border border-emerald-200 p-2 text-xs flex items-start gap-2">
          <CheckCircle2 className="size-4 text-emerald-700 shrink-0 mt-0.5" />
          <span>
            <span className="font-semibold text-emerald-800">Import complete.</span>{" "}
            The inventory list has been refreshed.
          </span>
        </div>
      )}

      {errs.length > 0 && (
        <div className="rounded-md bg-rose-50 border border-rose-200 p-2 text-xs">
          <div className="flex items-center gap-2 font-semibold text-rose-800 mb-1">
            <AlertCircle className="size-3.5" /> {errs.length} row{errs.length === 1 ? "" : "s"} skipped
          </div>
          <ul className="space-y-0.5 max-h-32 overflow-y-auto">
            {errs.map((e, i) => (
              <li key={i} data-testid={`import-items-error-${i}`} className="mono text-[11px]">
                Row {e.row}: {e.message}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


function StatCard({ label, value, color, bg }) {
  return (
    <div className={`rounded-md ${bg} p-2 text-center`}>
      <p className="overline text-[9px]">{label}</p>
      <p className={`mono font-bold text-xl ${color}`}>{value}</p>
    </div>
  );
}
